/**
 * LinkedInOutreachPage — /linkedin-presence/outreach
 *
 * Connection-request and DM templates for 4 personas (practitioner,
 * hiring manager, peer transitioner, recruiter). Do/Don't examples + the
 * principle that makes each work.
 *
 * All Access only — these templates are the highest-leverage content in
 * the product and convert best for subscribers actively job-searching.
 */

import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockIcon from "@mui/icons-material/Lock";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import TargetIcon from "@mui/icons-material/GpsFixed";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { OUTREACH_TEMPLATES, type OutreachTemplate } from "../data/linkedinPresenceContent";
import { L } from "../utils/localized";
import { useAuth } from "../hooks/useAuth";
import { getTierAccess } from "../utils/tier";
import ProUpsellNote from "../components/ProUpsellNote";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

function TemplateCard({ tpl, canAccess, isPro }: { tpl: OutreachTemplate; canAccess: boolean; isPro: boolean }) {
  const { t } = useTranslation("pages-products");
  const theme = useTheme();
  const navigate = useNavigate();
  const [copiedMain, setCopiedMain] = useState(false);
  const [copiedFollow, setCopiedFollow] = useState(false);

  const copy = (text: string, setter: (v: boolean) => void) => {
    navigator.clipboard.writeText(text);
    setter(true);
    setTimeout(() => setter(false), 2500);
  };

  if (!canAccess) {
    return (
      <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: { xs: 3, md: 4 }, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={500}>{tpl.persona}</Typography>
          <Chip icon={<LockIcon sx={{ fontSize: "0.8rem !important" }} />} label={t("LinkedInOutreachPage.allAccessChipLabel")} size="small" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, fontWeight: 500, fontSize: "0.68rem", height: 20 }} />
        </Stack>
        <Typography variant="body2" color="text.secondary">{t("LinkedInOutreachPage.goalLabel", { goal: tpl.goal })}</Typography>
        {isPro ? <ProUpsellNote /> : (
          <Paper elevation={0} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), border: `1px dashed ${alpha(theme.palette.primary.main, 0.35)}`, borderRadius: 2, p: 3, textAlign: "center", mt: 3 }}>
            <LockIcon sx={{ fontSize: 32, color: alpha(theme.palette.primary.main, 0.5), mb: 1.5 }} />
            <Typography variant="body1" fontWeight={500} gutterBottom>{t("LinkedInOutreachPage.unlockWithAllAccessMessage")}</Typography>
            <Button variant="contained" onClick={() => navigate("/pricing")} endIcon={<ArrowForwardIcon />} sx={{ bgcolor: theme.palette.primary.main, fontWeight: 500, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}>
              {t("LinkedInOutreachPage.seeAllAccessPricingButton")}
            </Button>
          </Paper>
        )}
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: { xs: 3, md: 4 }, mb: 3 }} id={tpl.slug}>
      {/* Header */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={3}>
        <Box flex={1}>
          <Typography variant="caption" fontWeight={700} sx={{ color: "text.disabled", letterSpacing: "0.08em", textTransform: "uppercase" }}>{t("LinkedInOutreachPage.personaLabel")}</Typography>
          <Typography variant="h6" fontWeight={500} sx={{ mb: 0.5 }}>{tpl.persona}</Typography>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TargetIcon sx={{ color: theme.palette.info.dark, fontSize: 16, mt: "3px", flexShrink: 0 }} />
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>{tpl.goal}</Typography>
          </Stack>
        </Box>
      </Stack>

      {/* Principle */}
      <Paper elevation={0} sx={{ bgcolor: alpha(theme.palette.warning.main, 0.06), border: `1px dashed ${alpha(theme.palette.warning.main, 0.4)}`, borderRadius: 2, p: 2, mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <LightbulbIcon sx={{ color: theme.palette.primary.main, fontSize: 18, mt: "2px", flexShrink: 0 }} />
          <Box>
            <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.primary.main, letterSpacing: "0.05em", textTransform: "uppercase", display: "block", mb: 0.25 }}>{t("LinkedInOutreachPage.thePrincipleLabel")}</Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{tpl.principle}</Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Template */}
      <Paper elevation={0} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`, borderRadius: 2, p: 2.5, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.primary.main, letterSpacing: "0.05em", textTransform: "uppercase" }}>{t("LinkedInOutreachPage.theTemplateLabel")}</Typography>
          <Tooltip title={copiedMain ? t("LinkedInOutreachPage.copiedTooltip") : t("LinkedInOutreachPage.copyTemplateTooltip")}>
            <IconButton size="small" onClick={() => copy(tpl.template, setCopiedMain)} sx={{ color: copiedMain ? theme.palette.success.dark : theme.palette.primary.main }}>
              {copiedMain ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.75, fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.82rem" }}>
          {tpl.template}
        </Typography>
      </Paper>

      {/* Do / Don't */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={tpl.followUp ? 3 : 0}>
        <Paper elevation={0} sx={{ flex: 1, bgcolor: alpha(theme.palette.success.main, 0.04), border: `1px solid ${alpha(theme.palette.success.main, 0.22)}`, borderRadius: 2, p: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <CheckCircleIcon sx={{ color: theme.palette.success.dark, fontSize: 16 }} />
            <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.success.dark, letterSpacing: "0.05em", textTransform: "uppercase" }}>{t("LinkedInOutreachPage.doLabel")}</Typography>
          </Stack>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7, fontSize: "0.82rem", color: "text.primary" }}>{tpl.doExample}</Typography>
        </Paper>
        <Paper elevation={0} sx={{ flex: 1, bgcolor: alpha(theme.palette.error.dark, 0.04), border: `1px solid ${alpha(theme.palette.error.dark, 0.22)}`, borderRadius: 2, p: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <CancelIcon sx={{ color: theme.palette.error.dark, fontSize: 16 }} />
            <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.error.dark, letterSpacing: "0.05em", textTransform: "uppercase" }}>{t("LinkedInOutreachPage.dontLabel")}</Typography>
          </Stack>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7, fontSize: "0.82rem", fontStyle: "italic", color: "text.secondary" }}>{tpl.dontExample}</Typography>
        </Paper>
      </Stack>

      {/* Follow-up */}
      {tpl.followUp && (
        <Paper elevation={0} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), border: `1px dashed ${alpha(theme.palette.primary.main, 0.35)}`, borderRadius: 2, p: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.primary.main, letterSpacing: "0.05em", textTransform: "uppercase" }}>{t("LinkedInOutreachPage.followUpLabel")}</Typography>
            <Tooltip title={copiedFollow ? t("LinkedInOutreachPage.copiedTooltip") : t("LinkedInOutreachPage.copyFollowUpTooltip")}>
              <IconButton size="small" onClick={() => copy(tpl.followUp!, setCopiedFollow)} sx={{ color: copiedFollow ? theme.palette.success.dark : theme.palette.primary.main }}>
                {copiedFollow ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7, fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.82rem" }}>
            {tpl.followUp}
          </Typography>
        </Paper>
      )}
    </Paper>
  );
}

export default function LinkedInOutreachPage() {
  const { t, i18n } = useTranslation("pages-products");
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const tierAccess = getTierAccess(user);
  const canAccess = tierAccess.canAccessProducts;
  const outreachTemplates = L(OUTREACH_TEMPLATES, i18n.language);

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      <Box sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`, borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`, py: { xs: 4, md: 6 } }}>
        <Container maxWidth="md">
          <Stack direction="row" spacing={1} alignItems="center" mb={3}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/linkedin-presence")} size="small" sx={{ color: "text.secondary", textTransform: "none", fontWeight: 500 }}>{t("LinkedInOutreachPage.breadcrumbLinkedInPresence")}</Button>
            <Typography variant="body2" color="text.disabled">›</Typography>
            <Typography variant="body2" color="text.secondary">{t("LinkedInOutreachPage.breadcrumbOutreachTemplates")}</Typography>
          </Stack>
          <Typography variant="h3" fontWeight={500} sx={{ letterSpacing: "-0.02em", mb: 1 }}>{t("LinkedInOutreachPage.pageTitle")}</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720, lineHeight: 1.65 }}>
            {t("LinkedInOutreachPage.pageIntro")}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        {!canAccess && (
          tierAccess.isPro ? <ProUpsellNote /> : (
            <Alert severity="info" icon={<LockIcon />} sx={{ mb: 4, borderRadius: 2 }} action={<Button size="small" onClick={() => navigate("/pricing")} endIcon={<ArrowForwardIcon />}>{t("LinkedInOutreachPage.seeAllAccessButton")}</Button>}>
              {t("LinkedInOutreachPage.gateNotice")}
            </Alert>
          )
        )}

        {outreachTemplates.map((tpl) => <TemplateCard key={tpl.slug} tpl={tpl} canAccess={canAccess} isPro={tierAccess.isPro} />)}

        <Divider sx={{ my: 5 }} />

        <Paper elevation={0} sx={{ p: 4, textAlign: "center", bgcolor: alpha(theme.palette.primary.main, 0.04), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, borderRadius: 3 }}>
          <Typography variant="h5" fontWeight={500} gutterBottom>{t("LinkedInOutreachPage.footerCtaTitle")}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 520, mx: "auto" }}>
            {t("LinkedInOutreachPage.footerCtaBody")}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
            <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />} onClick={() => navigate("/linkedin-presence/profile#monthly-linkedin-health-check")} sx={{ bgcolor: theme.palette.primary.main, fontWeight: 500, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}>
              {t("LinkedInOutreachPage.runMonthlyHealthCheckButton")}
            </Button>
            <Button variant="outlined" size="large" onClick={() => navigate("/resume-mastery")} sx={{ fontWeight: 500, borderColor: alpha(theme.palette.primary.main, 0.5), color: theme.palette.primary.main }}>
              {t("LinkedInOutreachPage.polishResumeNextButton")}
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
